import * as ROUTES from '../common/routes';
import Button from './Button';
import React, { useState } from 'react';
import SurveysList from './SurveysList';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslations } from '../common/hooks/useTranslations';
import Pagination from '@mui/material/Pagination';

const splitPages = (inputArray, perChunk) => {
  const result = inputArray.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / perChunk);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = []; // start a new chunk
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);

  return result;
};

interface IProps {
  data: any;
  defaultImage?: string;
  isSingleTheme?: boolean;
}

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      answersContainerStyle: {
        display: 'flex',
        flexDirection: 'column',
      },
      buttonViewMoreStyle: {
        width: '140px',
        minWidth: '140px',
      },
      paginationStyle: {
        marginTop: '20px',
        alignSelf: 'flex-end',
      },
    })
);

const ThemeAnswersPreview = ({
  data,
  defaultImage,
  isSingleTheme,
}: IProps): JSX.Element => {
  const classes = useStyles();
  const { search, pathname } = useLocation();
  const { themeId } = useParams();
  const navigate = useNavigate();

  const [pageNr, setPageNr] = useState(1);

  const viewMoreLabel = useTranslations('see_button');

  if (!data?.pages?.length) {
    return null;
  }

  const isMoreThan3 = data?.pages?.length > 3;
  const currentPagesData = isSingleTheme
    ? data.pages
    : data?.pages?.slice(0, 3);

  const onViewMoreClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const surveyId = data?.content?.uid;

    navigate({
      pathname: `${ROUTES.THEME}/${themeId}/${surveyId}`,
      search,
    });
  };

  const pageSize = 20;
  const splitPagesData = splitPages(currentPagesData, pageSize);

  const getPageData = () => {
    if (currentPagesData.length > pageSize) {
      const currentPageData = splitPagesData[pageNr - 1];

      return currentPageData;
    }

    return currentPagesData;
  };

  const currentPageData = getPageData() || [];
  const count = splitPagesData?.length;

  const getPagination = () => {
    const onChange = (
      event: React.ChangeEvent<unknown>,
      currentPage: number
    ) => {
      setPageNr(currentPage);
    };

    if (currentPagesData.length > pageSize) {
      return (
        <Pagination
          className={classes.paginationStyle}
          count={count}
          showFirstButton
          showLastButton
          onChange={onChange}
        />
      );
    }

    return null;
  };

  return (
    <div className={classes.answersContainerStyle}>
      <SurveysList data={currentPageData} defaultImage={defaultImage} />
      {isMoreThan3 && !isSingleTheme && (
        <Button
          text={viewMoreLabel}
          onClick={onViewMoreClick}
          className={classes.buttonViewMoreStyle}
        />
      )}
      {getPagination()}
    </div>
  );
};

export default ThemeAnswersPreview;
